// Preparación de archivos para el upload de comprobantes:
// imágenes se comprimen client-side (canvas, máx 1600 px, JPEG q0.8) —
// clave para clientes con datos móviles limitados. PDF pasa directo.

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;
const MAX_BASE64_LENGTH = 8 * 1024 * 1024; // debe coincidir con el backend
// Bytes reales equivalentes al tope de base64 (~6 MB): se chequea ANTES de leer
// el archivo, así un PDF gigante no se carga entero en memoria para después
// rechazarse (en un celular de gama baja eso es una pestaña muerta).
const MAX_FILE_BYTES = Math.floor((MAX_BASE64_LENGTH * 3) / 4);
// Las imágenes se recomprimen (1600 px, JPEG q0.8), así que pueden entrar más
// grandes; igual se corta el original absurdo antes de decodificarlo.
const MAX_IMAGE_SOURCE_BYTES = 25 * 1024 * 1024;

export class FileTooBigError extends Error {}
export class UnsupportedFileError extends Error {}

export async function prepareFileForUpload(file) {
  if (!file) throw new UnsupportedFileError();

  if (file.type === "application/pdf") {
    if (file.size > MAX_FILE_BYTES) throw new FileTooBigError();
    const dataBase64 = await readAsBase64(file);
    if (dataBase64.length > MAX_BASE64_LENGTH) throw new FileTooBigError();
    return { fileName: file.name || "comprobante.pdf", mimeType: "application/pdf", dataBase64 };
  }

  if (file.type.startsWith("image/")) {
    if (file.size > MAX_IMAGE_SOURCE_BYTES) throw new FileTooBigError();
    const dataUrl = await compressImage(file);
    const dataBase64 = dataUrl.split(",")[1] || "";
    if (dataBase64.length > MAX_BASE64_LENGTH) throw new FileTooBigError();
    const baseName = (file.name || "comprobante").replace(/\.[^.]+$/, "");
    return { fileName: `${baseName}.jpg`, mimeType: "image/jpeg", dataBase64 };
  }

  throw new UnsupportedFileError();
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(new UnsupportedFileError());
    reader.readAsDataURL(file);
  });
}

async function compressImage(file) {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) {
    // Formato de imagen que el browser no decodifica: mandar tal cual
    const base64 = await readAsBase64(file);
    return `data:${file.type};base64,${base64}`;
  }

  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}
